import React, { useState, useEffect, useMemo } from 'react';
import { collection, addDoc, updateDoc, doc, onSnapshot, deleteDoc } from 'firebase/firestore';
import { db } from '../App';
import { appId } from '../config/firebaseConfig';
import useConfirmationModal from '../components/common/useConfirmationModal';

const formatCPFouCNPJ = (value, tipoPessoa) => {
    const clean = value.replace(/\D/g, '');
    if (tipoPessoa === 'PF') {
        if (clean.length <= 3) return clean;
        if (clean.length <= 6) return `${clean.slice(0, 3)}.${clean.slice(3)}`;
        if (clean.length <= 9) return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6)}`;
        return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6, 9)}-${clean.slice(9, 11)}`;
    } else {
        if (clean.length <= 2) return clean;
        if (clean.length <= 5) return `${clean.slice(0, 2)}.${clean.slice(2)}`;
        if (clean.length <= 8) return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5)}`;
        if (clean.length <= 12) return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5, 8)}/${clean.slice(8)}`;
        return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5, 8)}/${clean.slice(8, 12)}-${clean.slice(12, 14)}`;
    }
};

const formatPhone = (value) => {
    const clean = value.replace(/\D/g, '');
    if (clean.length <= 2) return clean.length > 0 ? `(${clean}` : '';
    if (clean.length <= 6) return `(${clean.slice(0, 2)}) ${clean.slice(2)}`;
    if (clean.length <= 10) return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`;
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7, 11)}`;
};

const FormModal = ({ title, children, onClose, onSave, onDelete, loading }) => (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-[110] p-4 font-sans backdrop-blur-sm">
        <div className="bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col border border-slate-700 animate-fadeIn">
            <header className="p-6 border-b border-slate-700 flex justify-between items-center flex-shrink-0">
                <h3 className="font-bold text-2xl text-cyan-300 uppercase tracking-tight">{title}</h3>
                <button onClick={onClose} disabled={loading} className="text-slate-400 hover:text-white text-3xl transition-colors disabled:opacity-30">×</button>
            </header>
            <div className="p-6 overflow-y-auto flex-grow space-y-4 custom-scrollbar text-left">{children}</div>
            <footer className="p-6 border-t border-slate-700 flex-shrink-0 flex gap-4">
                {onDelete && (
                    <button type="button" onClick={onDelete} disabled={loading} className="bg-red-600 hover:bg-red-500 text-white font-bold py-3 px-6 rounded-md transition-colors uppercase text-xs tracking-widest mr-auto disabled:opacity-50">Deletar</button>
                )}
                <button type="button" onClick={onClose} disabled={loading} className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 rounded-md transition-colors uppercase text-xs tracking-widest disabled:opacity-50">Cancelar</button>
                <button type="button" onClick={onSave} disabled={loading} className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-3 rounded-md transition-colors uppercase text-xs tracking-widest disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? (
                        <>
                            <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></div>
                            <span>Processando...</span>
                        </>
                    ) : (
                        <span>Salvar</span>
                    )}
                </button>
            </footer>
        </div>
    </div>
);

export default function HomePage({ permissionLevel, navigateTo }) {
    const { ConfirmationModal, showConfirmation } = useConfirmationModal();
    const [clientsContrato, setClientsContrato] = useState([]);
    const [clientsAvulsos, setClientsAvulsos] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit'
    const [editingClientId, setEditingClientId] = useState('');

    // Form fields
    const [cnpj, setCnpj] = useState('');
    const [tipoPessoa, setTipoPessoa] = useState('PJ'); // 'PJ' | 'PF'
    const [name, setName] = useState('');
    const [nomeFantasia, setNomeFantasia] = useState('');
    const [endereco, setEndereco] = useState('');
    const [telefone, setTelefone] = useState('');
    const [email, setEmail] = useState('');
    const [clientType, setClientType] = useState('clients'); // 'clients' (contrato) or 'clients-avulsos' (avulso)
    const [ativo, setAtivo] = useState(true);
    
    const [visibleTickets, setVisibleTickets] = useState(true);
    const [visibleAssets, setVisibleAssets] = useState(true);
    const [vhsysId, setVhsysId] = useState('');

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [apiError, setApiError] = useState('');
    const [apiSuccess, setApiSuccess] = useState('');

    const [filterType, setFilterType] = useState('all'); // 'all' | 'contrato' | 'avulso'
    const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'ativo' | 'inativo'
    const [filterVhsys, setFilterVhsys] = useState('all'); // 'all' | 'synced' | 'not_synced'

    // Fetch lists
    useEffect(() => {
        const unsubContrato = onSnapshot(collection(db, `/artifacts/${appId}/public/data/clients`), (snap) => {
            setClientsContrato(snap.docs.map(d => ({ id: d.id, ...d.data(), type: 'contrato' })));
        });
        const unsubAvulsos = onSnapshot(collection(db, `/artifacts/${appId}/public/data/clients-avulsos`), (snap) => {
            setClientsAvulsos(snap.docs.map(d => ({ id: d.id, ...d.data(), type: 'avulso' })));
        });
        return () => {
            unsubContrato();
            unsubAvulsos();
        };
    }, []);

    // Merge and sort alphabetically
    const combinedClients = useMemo(() => {
        const all = [...clientsContrato, ...clientsAvulsos];
        return all.sort((a, b) => (a.nomeFantasia || a.name || '').localeCompare(b.nomeFantasia || b.name || ''));
    }, [clientsContrato, clientsAvulsos]);

    // Filter by type, situation, VHSYS and search
    const filteredClients = useMemo(() => {
        let list = combinedClients;
        
        if (filterType !== 'all') {
            list = list.filter(c => c.type === filterType);
        }
        
        if (filterStatus !== 'all') {
            const wantActive = filterStatus === 'ativo';
            list = list.filter(c => (c.ativo !== false) === wantActive);
        }

        if (filterVhsys !== 'all') {
            if (filterVhsys === 'synced') {
                list = list.filter(c => !!c.vhsysId);
            } else if (filterVhsys === 'not_synced') {
                list = list.filter(c => !c.vhsysId);
            }
        }
        
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(c => 
                (c.name || '').toLowerCase().includes(term) ||
                (c.nomeFantasia || '').toLowerCase().includes(term) ||
                (c.cnpj || '').toLowerCase().includes(term) ||
                (c.vhsysId && String(c.vhsysId).toLowerCase().includes(term))
            );
        }
        
        return list;
    }, [combinedClients, filterType, filterStatus, filterVhsys, searchTerm]);

    const handleSearchCNPJ = async () => {
        const cleanCnpj = cnpj.replace(/\D/g, '');
        if (cleanCnpj.length !== 14) {
            setApiError('O CNPJ deve conter 14 dígitos.');
            return;
        }
        setLoading(true);
        setApiError('');
        setApiSuccess('');
        try {
            const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cleanCnpj}`);
            if (!res.ok) throw new Error('CNPJ não encontrado.');
            const data = await res.json();
            setName(data.razao_social || '');
            setNomeFantasia(data.nome_fantasia || data.razao_social || '');
            setEmail(data.email || '');
            
            const ddd = data.ddd_telefone_1 ? data.ddd_telefone_1.substring(0, 2) : '';
            const tel = data.ddd_telefone_1 ? data.ddd_telefone_1.substring(2) : '';
            if (ddd && tel) {
                setTelefone(formatPhone(`${ddd}${tel}`));
            } else if (data.ddd_telefone_1) {
                setTelefone(formatPhone(data.ddd_telefone_1));
            }
            setCnpj(formatCPFouCNPJ(cleanCnpj, 'PJ'));

            const addressParts = [
                data.logradouro && `${data.logradouro}, ${data.numero || 'S/N'}${data.complemento ? ` - ${data.complemento}` : ''}`,
                data.bairro,
                data.municipio && `${data.municipio} - ${data.uf}`,
                data.cep && `CEP ${data.cep}`
            ].filter(Boolean);
            setEndereco(addressParts.join(', '));
            setApiSuccess('Dados do CNPJ preenchidos!');
        } catch (error) {
            console.error("Erro CNPJ:", error);
            setApiError('Não foi possível carregar o CNPJ automaticamente. Digite manual.');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenAddModal = () => {
        setModalMode('add');
        setEditingClientId('');
        setCnpj('');
        setTipoPessoa('PJ');
        setName('');
        setNomeFantasia('');
        setEndereco('');
        setTelefone('');
        setEmail('');
        setClientType('clients');
        setAtivo(true);
        setVisibleTickets(true);
        setVisibleAssets(true);
        setVhsysId('');
        setApiSuccess('');
        setApiError('');
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (client) => {
        setModalMode('edit');
        setEditingClientId(client.id);
        setTipoPessoa(client.tipoPessoa || 'PJ');
        setCnpj(formatCPFouCNPJ(client.cnpj || '', client.tipoPessoa || 'PJ'));
        setName(client.name || '');
        setNomeFantasia(client.nomeFantasia || client.name || '');
        setEndereco(client.endereco || '');
        setTelefone(formatPhone(client.telefone || ''));
        setEmail(client.email || '');
        setClientType(client.type === 'contrato' ? 'clients' : 'clients-avulsos');
        setAtivo(client.ativo !== false);
        setVisibleTickets(client.visibilidadeModulos?.tickets !== false);
        setVisibleAssets(client.visibilidadeModulos?.assets !== false);
        setVhsysId(client.vhsysId || '');
        setApiSuccess('');
        setApiError('');
        setIsModalOpen(true);
    };

    const handleSaveClient = async () => {
        if (!name.trim()) {
            alert('Preencha pelo menos a Razão Social/Nome.');
            return;
        }

        const cleanIncoming = cnpj.replace(/\D/g, '');
        if (cleanIncoming) {
            const isDuplicate = [...clientsContrato, ...clientsAvulsos].some(c => {
                if (modalMode === 'edit' && c.id === editingClientId) return false;
                return c.cnpj && c.cnpj.replace(/\D/g, '') === cleanIncoming;
            });
            if (isDuplicate) {
                alert('Este CPF/CNPJ já está cadastrado para outro cliente.');
                return;
            }
        }

        const dataToSave = {
            name: name.trim(),
            nomeFantasia: (nomeFantasia || name).trim(),
            cnpj: cnpj.trim(),
            tipoPessoa,
            endereco: endereco.trim(),
            telefone: telefone.trim(),
            email: email.trim(),
            ativo,
            visibilidadeModulos: {
                tickets: visibleTickets,
                assets: visibleAssets
            },
            vhsysId: vhsysId ? String(vhsysId) : '',
            updatedAt: new Date()
        };

        const performSave = async (updatedData) => {
            setLoading(true);
            try {
                if (modalMode === 'edit') {
                    const collName = clientType; 
                    const docRef = doc(db, `/artifacts/${appId}/public/data/${collName}`, editingClientId);
                    await updateDoc(docRef, updatedData);
                    setMessage('Cliente atualizado com sucesso!');
                } else {
                    const collPath = `/artifacts/${appId}/public/data/${clientType}`;
                    await addDoc(collection(db, collPath), {
                        ...updatedData,
                        createdAt: new Date()
                    });
                    setMessage('Cliente adicionado com sucesso!');
                }
                setIsModalOpen(false);
                setTimeout(() => setMessage(''), 4000);
            } catch (err) {
                console.error("Erro ao salvar cliente:", err);
                alert("Erro ao salvar cliente.");
            } finally {
                setLoading(false);
            }
        };

        // Sincronização automática com VHSYS
        if (cnpj.trim() && !dataToSave.vhsysId) {
            setLoading(true);
            try {
                const { getFunctions, httpsCallable } = await import('firebase/functions');
                const functions = getFunctions();
                const buscarFn = httpsCallable(functions, 'buscarClientePorCnpj');
                const searchRes = await buscarFn({ cnpj: cnpj.trim() });
                console.log("DEBUG VHSYS buscarClientePorCnpj:", searchRes.data);
                
                if (searchRes.data && searchRes.data.success) {
                    const linkedId = searchRes.data.client.id_cliente;
                    dataToSave.vhsysId = String(linkedId);
                    setVhsysId(String(linkedId));
                    await performSave(dataToSave);
                } else {
                    setLoading(false);
                    const debugInfo = searchRes.data?.debug ? JSON.stringify(searchRes.data.debug, null, 2) : "Sem dados adicionais";
                    console.warn("DEBUG INFO:", debugInfo);
                    showConfirmation({
                        title: "Integração VHSYS",
                        message: `Cliente não localizado no VHSYS. (Total na pág: ${searchRes.data?.debug?.total_on_page || 0})\n\nDeseja cadastrá-lo automaticamente no VHSYS?`,
                        confirmText: "Cadastrar",
                        onConfirm: async () => {
                            setLoading(true);
                            try {
                                const cadastrarFn = httpsCallable(functions, 'cadastrarClienteNoVhsys');
                                const createRes = await cadastrarFn({ clientData: dataToSave });
                                if (createRes.data && createRes.data.success) {
                                    const newId = createRes.data.id_cliente;
                                    dataToSave.vhsysId = String(newId);
                                    setVhsysId(String(newId));
                                }
                            } catch (err) {
                                console.error("Erro na integração VHSYS:", err);
                            }
                            await performSave(dataToSave);
                        },
                        onCancel: async () => {
                            await performSave(dataToSave);
                        }
                    });
                }
            } catch (err) {
                console.error("Erro na integração VHSYS:", err);
                alert("Aviso: Não foi possível sincronizar com o VHSYS: " + err.message);
                await performSave(dataToSave);
            }
        } else {
            await performSave(dataToSave);
        }
    };

    const handleDeleteClient = () => {
        showConfirmation({
            title: "Deletar Cliente",
            message: "Tem certeza de que deseja deletar este cliente? Todos os dados associados a ele serão perdidos permanentemente.",
            confirmText: "Deletar",
            confirmColor: "bg-red-500",
            onConfirm: async () => {
                setLoading(true);
                try {
                    const collName = clientType;
                    const docRef = doc(db, `/artifacts/${appId}/public/data/${collName}`, editingClientId);
                    await deleteDoc(docRef);
                    setMessage('Cliente deletado com sucesso!');
                    setIsModalOpen(false);
                    setTimeout(() => setMessage(''), 4000);
                } catch (err) {
                    console.error("Erro ao deletar cliente:", err);
                    alert("Erro ao deletar cliente.");
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const isEditAllowed = permissionLevel === 'edit';
    const isAiSearchAllowed = permissionLevel === 'read' || permissionLevel === 'edit';

    return (
        <div className="relative pt-8 md:pt-12 px-4 md:px-8 pb-8 font-sans text-white bg-slate-900 min-h-screen">
            {isAiSearchAllowed && (
                <div className="fixed top-4 right-4 z-50">
                    <button
                        onClick={() => navigateTo('ai-search')}
                        className="p-3 rounded-full bg-slate-800 text-cyan-300 shadow-lg border border-slate-700 hover:bg-slate-700 transition-colors"
                        title="Pesquisa Inteligente"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 00-2.456 2.456z" />
                        </svg>
                    </button>
                </div>
            )}

            <header className="mb-8 flex justify-between items-center flex-wrap gap-4">
                <div>
                    <h1 className="text-5xl font-bold text-cyan-400">Cadastro de Clientes</h1>
                    <p className="text-slate-400 mt-2">Visualize e gerencie todos os clientes em ordem alfabética.</p>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                    <select
                        value={filterType}
                        onChange={e => setFilterType(e.target.value)}
                        className="bg-slate-800 border border-slate-700 rounded-md p-3 text-white outline-none focus:ring-1 focus:ring-cyan-500 font-semibold"
                    >
                        <option value="all">Todos os Tipos</option>
                        <option value="contrato">Contrato</option>
                        <option value="avulso">Avulso</option>
                    </select>

                    <select
                        value={filterStatus}
                        onChange={e => setFilterStatus(e.target.value)}
                        className="bg-slate-800 border border-slate-700 rounded-md p-3 text-white outline-none focus:ring-1 focus:ring-cyan-500 font-semibold"
                    >
                        <option value="all">Todas as Situações</option>
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Inativo</option>
                    </select>

                    <select
                        value={filterVhsys}
                        onChange={e => setFilterVhsys(e.target.value)}
                        className="bg-slate-800 border border-slate-700 rounded-md p-3 text-white outline-none focus:ring-1 focus:ring-cyan-500 font-semibold"
                    >
                        <option value="all">Status VHSYS (Todos)</option>
                        <option value="synced">Sincronizado</option>
                        <option value="not_synced">Não Integrado</option>
                    </select>

                    <input 
                        type="text" 
                        placeholder="Pesquisar cliente, CNPJ ou VHSYS..." 
                        value={searchTerm} 
                        onChange={e => setSearchTerm(e.target.value)} 
                        className="bg-slate-800 border border-slate-700 rounded-md p-3 text-white w-64 shadow-inner outline-none focus:ring-1 focus:ring-cyan-500" 
                    />
                </div>
            </header>

            {message && (
                <div className="mb-6 p-4 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-lg text-sm text-center font-bold">
                    {message}
                </div>
            )}

            {filteredClients.length === 0 ? (
                <div className="bg-slate-800/40 border border-slate-750 rounded-xl p-12 text-center text-slate-500 italic">
                    Nenhum cliente cadastrado ou correspondente à pesquisa.
                </div>
            ) : (
                <div className="space-y-3 max-w-7xl mx-auto">
                    {filteredClients.map(client => (
                        <div 
                            key={client.id} 
                            onClick={() => isEditAllowed && handleOpenEditModal(client)}
                            className={`bg-slate-800 hover:bg-slate-750/80 px-6 py-4 rounded-xl border border-slate-700 hover:border-cyan-550 shadow-md flex flex-col lg:flex-row lg:items-center justify-between gap-4 transition-all group ${isEditAllowed ? 'cursor-pointer' : ''}`}
                        >
                            <div className="flex-grow min-w-0 text-left">
                                <h3 className="text-xl font-bold text-cyan-300 group-hover:text-cyan-200 truncate mb-1">
                                    {client.nomeFantasia || client.name}
                                </h3>
                                {client.nomeFantasia && client.nomeFantasia !== client.name && (
                                    <p className="text-slate-450 text-xs truncate"><strong>Razão Social:</strong> {client.name}</p>
                                )}
                                <p className="text-xs text-slate-400 truncate mt-1"><strong>Endereço:</strong> {client.endereco || 'Não informado'}</p>
                            </div>
                            
                            <div className="flex flex-wrap md:flex-nowrap gap-4 md:gap-8 text-sm text-slate-300 flex-shrink-0 text-left items-center">
                                <div className="w-40 truncate">
                                    <span className="text-[9px] text-slate-500 block uppercase font-bold tracking-wider mb-0.5">
                                        {client.tipoPessoa === 'PF' ? 'CPF' : 'CNPJ'}
                                    </span>
                                    {client.cnpj || 'Não informado'}
                                </div>
                                <div className="w-36 truncate">
                                    <span className="text-[9px] text-slate-500 block uppercase font-bold tracking-wider mb-0.5">Telefone</span>
                                    {client.telefone || 'Não informado'}
                                </div>
                                <div className="w-48 truncate">
                                    <span className="text-[9px] text-slate-500 block uppercase font-bold tracking-wider mb-0.5">E-mail</span>
                                    {client.email || 'Não informado'}
                                </div>
                                <div className="w-24 flex-shrink-0">
                                    <span className="text-[9px] text-slate-500 block uppercase font-bold tracking-wider mb-1">Tipo</span>
                                    <span className={`text-[9px] px-2 py-0.5 rounded font-black uppercase tracking-wider inline-block text-center w-20 ${client.type === 'contrato' ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'}`}>
                                        {client.type === 'contrato' ? 'Contrato' : 'Avulso'}
                                    </span>
                                </div>
                                <div className="w-24 flex-shrink-0">
                                    <span className="text-[9px] text-slate-500 block uppercase font-bold tracking-wider mb-1">Situação</span>
                                    <span className={`text-[9px] px-2 py-0.5 rounded font-black uppercase tracking-wider inline-block text-center w-20 ${client.ativo !== false ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                                        {client.ativo !== false ? 'Ativo' : 'Inativo'}
                                    </span>
                                </div>
                                <div className="w-28 flex-shrink-0">
                                    <span className="text-[9px] text-slate-500 block uppercase font-bold tracking-wider mb-1">VHSYS</span>
                                    {client.vhsysId ? (
                                        <span 
                                            className="text-[9px] px-2 py-0.5 rounded font-mono font-black uppercase tracking-wider inline-block text-center w-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 truncate"
                                            title={`ID VHSYS: ${client.vhsysId}`}
                                        >
                                            ID: {client.vhsysId}
                                        </span>
                                    ) : (
                                        <span className="text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wider inline-block text-center w-full bg-slate-700/40 text-slate-400 border border-slate-600/40">
                                            Não Integrado
                                        </span>
                                    )}
                                </div>
                            </div>
                            {isEditAllowed && (
                                <div className="flex items-center justify-end lg:justify-center flex-shrink-0 text-slate-500 group-hover:text-cyan-400 transition-colors ml-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Modal de Cadastro/Edição */}
            {isModalOpen && (
                <FormModal 
                    title={modalMode === 'add' ? "Adicionar Novo Cliente" : "Editar Cliente"} 
                    onClose={() => setIsModalOpen(false)} 
                    onSave={handleSaveClient}
                    onDelete={(modalMode === 'edit' && isEditAllowed) ? handleDeleteClient : undefined}
                    loading={loading}
                >
                    <div className="flex items-center justify-between mb-4 bg-slate-900/40 p-4 rounded-xl border border-slate-700/50">
                        <span className="text-xs font-semibold text-slate-300">Tipo de Inscrição</span>
                        <div className="flex bg-slate-800 rounded-lg p-0.5 border border-slate-700">
                            <button
                                type="button"
                                onClick={() => {
                                    setTipoPessoa('PJ');
                                    setCnpj('');
                                }}
                                className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${tipoPessoa === 'PJ' ? 'bg-cyan-500 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
                            >
                                Pessoa Jurídica (CNPJ)
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setTipoPessoa('PF');
                                    setCnpj('');
                                }}
                                className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${tipoPessoa === 'PF' ? 'bg-cyan-500 text-white shadow-md' : 'text-slate-400 hover:text-white'}`}
                            >
                                Pessoa Física (CPF)
                            </button>
                        </div>
                    </div>

                    {tipoPessoa === 'PJ' && (
                        <div className="bg-slate-900/40 p-4 rounded-xl border border-slate-700/50 space-y-3 mb-4">
                            <label className="block text-xs font-bold text-cyan-400 uppercase tracking-widest">Preenchimento via CNPJ</label>
                            <div className="flex gap-2">
                                <input 
                                    type="text" 
                                    placeholder="CNPJ (apenas números)" 
                                    value={cnpj} 
                                    onChange={(e) => setCnpj(formatCPFouCNPJ(e.target.value, 'PJ'))} 
                                    className="flex-grow bg-slate-700 border border-slate-600 rounded-md p-2 text-white outline-none focus:ring-2 focus:ring-cyan-400"
                                />
                                <button 
                                    type="button" 
                                    onClick={handleSearchCNPJ}
                                    disabled={loading || !cnpj.trim()}
                                    className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-800 text-white px-4 py-2 rounded-md font-bold transition-all uppercase text-xs tracking-wider"
                                >
                                    {loading ? 'Buscando...' : 'Buscar'}
                                </button>
                            </div>
                            {apiError && <p className="text-red-400 text-xs">{apiError}</p>}
                            {apiSuccess && <p className="text-emerald-400 text-xs">{apiSuccess}</p>}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">
                                {tipoPessoa === 'PF' ? 'Nome Completo *' : 'Razão Social *'}
                            </label>
                            <input 
                                type="text" 
                                required 
                                value={name} 
                                onChange={(e) => setName(e.target.value)} 
                                placeholder={tipoPessoa === 'PF' ? "Nome da pessoa física" : "Nome oficial da empresa"} 
                                className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none text-white text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">Nome Fantasia (Exibição) *</label>
                            <input 
                                type="text" 
                                required
                                value={nomeFantasia} 
                                onChange={(e) => setNomeFantasia(e.target.value)} 
                                placeholder="Como aparecerá no sistema" 
                                className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none text-white text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">
                                {tipoPessoa === 'PF' ? 'CPF' : 'CNPJ'}
                            </label>
                            <input 
                                type="text" 
                                value={cnpj} 
                                onChange={(e) => setCnpj(formatCPFouCNPJ(e.target.value, tipoPessoa))} 
                                placeholder={tipoPessoa === 'PF' ? "000.000.000-00" : "00.000.000/0000-00"} 
                                className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none text-white text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">Telefone</label>
                            <input 
                                type="text" 
                                value={telefone} 
                                onChange={(e) => setTelefone(formatPhone(e.target.value))} 
                                placeholder="Telefone ou WhatsApp" 
                                className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none text-white text-sm"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs font-semibold text-slate-300 mb-1">Endereço</label>
                            <input 
                                type="text" 
                                value={endereco} 
                                onChange={(e) => setEndereco(e.target.value)} 
                                placeholder="Rua, número, bairro, cidade, CEP..." 
                                className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none text-white text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">E-mail</label>
                            <input 
                                type="email" 
                                value={email} 
                                onChange={(e) => setEmail(e.target.value)} 
                                placeholder="Email para contato" 
                                className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none text-white text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-300 mb-1">Cadastrar Em</label>
                            <select 
                                value={clientType} 
                                onChange={(e) => setClientType(e.target.value)} 
                                disabled={modalMode === 'edit'}
                                className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none text-white text-sm font-semibold disabled:opacity-55 disabled:cursor-not-allowed"
                            >
                                <option value="clients">Clientes Contrato</option>
                                <option value="clients-avulsos">Cliente Avulso</option>
                            </select>
                        </div>

                        <div className="md:col-span-2 border-t border-slate-700 pt-4 mt-2">
                            <h4 className="text-xs font-bold text-cyan-400 uppercase tracking-wider mb-3">Visibilidade por Módulo</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <label className="flex items-center gap-2.5 p-2.5 bg-slate-900/40 hover:bg-slate-700/40 cursor-pointer rounded-lg transition-all border border-slate-700">
                                    <input 
                                        type="checkbox" 
                                        checked={visibleTickets} 
                                        onChange={(e) => setVisibleTickets(e.target.checked)}
                                        className="form-checkbox text-cyan-500 rounded h-4.5 w-4.5 border-slate-650 bg-slate-800" 
                                    />
                                    <div>
                                        <span className="text-white text-xs font-bold block">Chamados</span>
                                        <span className="text-slate-500 text-[10px]">Exibir no seletor de chamados/tickets</span>
                                    </div>
                                </label>

                                <label className="flex items-center gap-2.5 p-2.5 bg-slate-900/40 hover:bg-slate-700/40 cursor-pointer rounded-lg transition-all border border-slate-700">
                                    <input 
                                        type="checkbox" 
                                        checked={visibleAssets} 
                                        onChange={(e) => setVisibleAssets(e.target.checked)}
                                        className="form-checkbox text-cyan-500 rounded h-4.5 w-4.5 border-slate-650 bg-slate-800" 
                                    />
                                    <div>
                                        <span className="text-white text-xs font-bold block">Ativos</span>
                                        <span className="text-slate-500 text-[10px]">Exibir no seletor de ativos/equipamentos</span>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div className="md:col-span-2 border-t border-slate-700 pt-4 mt-2 bg-slate-900/20 p-4 rounded-xl border border-slate-700/30">
                            <h4 className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-1">Sincronização VHSYS</h4>
                            <div className="flex items-center justify-between gap-3">
                                <span className="text-xs text-slate-400">
                                    {vhsysId ? (
                                        <>Vinculado ao VHSYS (ID: <strong className="text-white font-mono">{vhsysId}</strong>)</>
                                    ) : (
                                        <>Não integrado. Sincroniza via {tipoPessoa === 'PF' ? 'CPF' : 'CNPJ'} ao salvar.</>
                                    )}
                                </span>
                                {vhsysId && (
                                    <button
                                        type="button"
                                        onClick={() => setVhsysId('')}
                                        className="text-xs font-bold text-red-400 hover:text-red-300 underline"
                                    >
                                        Remover Vinculação
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </FormModal>
            )}

            {/* Floating Action Button (FAB) */}
            {isEditAllowed && (
                <button 
                    onClick={handleOpenAddModal} 
                    className="fixed bottom-10 right-10 bg-cyan-500 hover:bg-cyan-600 text-white rounded-full h-14 w-14 shadow-2xl transition-all transform hover:scale-110 active:scale-95 z-30 flex items-center justify-center font-sans"
                    title="Adicionar Novo Cliente"
                >
                    <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                </button>
            )}

            <ConfirmationModal />
        </div>
    );
}